import { ROUTE_COLLECT, ROUTE_COLLECTIONS } from '@klear/constants'
import toString from 'lodash/toString'

export const COLLECTION_TYPE = {
  FEES: 'fees',
  ADVANCE_RECOVERY: 'advance-recovery'
} as const

export type CollectionType = (typeof COLLECTION_TYPE)[keyof typeof COLLECTION_TYPE]

export const COLLECTION_TYPE_LABELS: Record<CollectionType, string> = {
  [COLLECTION_TYPE.FEES]: 'Fee collection',
  [COLLECTION_TYPE.ADVANCE_RECOVERY]: 'Advance recovery'
}

export const COLLECTION_STEP_ID = {
  SELECT_COLLECTION_TYPE: 'select-collection-type',
  SELECT_TRADER: 'select-trader',
  SELECT_ITEMS: 'select-items',
  RECALCULATE_ALLOCATION: 'recalculate-allocation',
  PAYMENT_DETAILS: 'payment-details',
  REVIEW: 'review'
} as const

export type CollectionStepId = (typeof COLLECTION_STEP_ID)[keyof typeof COLLECTION_STEP_ID]

const STEP_ORDER_ADVANCE_RECOVERY: CollectionStepId[] = [
  COLLECTION_STEP_ID.SELECT_COLLECTION_TYPE,
  COLLECTION_STEP_ID.SELECT_TRADER,
  COLLECTION_STEP_ID.SELECT_ITEMS,
  COLLECTION_STEP_ID.RECALCULATE_ALLOCATION,
  COLLECTION_STEP_ID.REVIEW
]

const STEP_ORDER_ADVANCE_RECOVERY_NO_RECALC: CollectionStepId[] = [
  COLLECTION_STEP_ID.SELECT_COLLECTION_TYPE,
  COLLECTION_STEP_ID.SELECT_TRADER,
  COLLECTION_STEP_ID.SELECT_ITEMS,
  COLLECTION_STEP_ID.REVIEW
]

const STEP_ORDER_FEES: CollectionStepId[] = [
  COLLECTION_STEP_ID.SELECT_COLLECTION_TYPE,
  COLLECTION_STEP_ID.SELECT_TRADER,
  COLLECTION_STEP_ID.SELECT_ITEMS,
  COLLECTION_STEP_ID.REVIEW
]

export function getCollectionStepOrder(
  collectionType: CollectionType | null,
  options?: { includeRecalculateAllocation?: boolean }
): CollectionStepId[] {
  if (collectionType === COLLECTION_TYPE.ADVANCE_RECOVERY) {
    return options?.includeRecalculateAllocation ? STEP_ORDER_ADVANCE_RECOVERY : STEP_ORDER_ADVANCE_RECOVERY_NO_RECALC
  }
  return STEP_ORDER_FEES
}

export const COLLECTION_QUERY_REFERENCE_ID = 'referenceId'
export const COLLECTION_QUERY_TRADER_ID = 'traderId'
export const COLLECTION_QUERY_COLLECTION_TYPE = 'collectionType'

export type CollectDeepLinkParams = {
  collectionType: CollectionType
  nextStep: CollectionStepId
  traderId?: number
  referenceId?: number
}

export function buildCollectDeepLinkHref({
  collectionType,
  nextStep,
  traderId,
  referenceId
}: CollectDeepLinkParams): string {
  const params = new URLSearchParams()
  if (referenceId != null && referenceId > 0) {
    params.set(COLLECTION_QUERY_REFERENCE_ID, toString(referenceId))
  }
  if (traderId != null && traderId > 0) {
    params.set(COLLECTION_QUERY_TRADER_ID, toString(traderId))
  }
  params.set(COLLECTION_QUERY_COLLECTION_TYPE, collectionType)
  return `/${ROUTE_COLLECTIONS}/${ROUTE_COLLECT}/${nextStep}?${params.toString()}`
}

export function buildCollectStartHref(collectionType: CollectionType, nextStep: CollectionStepId): string {
  return buildCollectDeepLinkHref({ collectionType, nextStep })
}

export const COLLECTION_STEP_LABELS: Record<CollectionStepId, string> = {
  [COLLECTION_STEP_ID.SELECT_COLLECTION_TYPE]: 'Collection type',
  [COLLECTION_STEP_ID.SELECT_TRADER]: 'Trader',
  [COLLECTION_STEP_ID.SELECT_ITEMS]: 'Select items',
  [COLLECTION_STEP_ID.RECALCULATE_ALLOCATION]: 'Recalculate allocation',
  [COLLECTION_STEP_ID.PAYMENT_DETAILS]: 'Payment details',
  [COLLECTION_STEP_ID.REVIEW]: 'Review & collect'
}
